// ─── Setup — the rules, separate from the screen ─────────────────────────────
//
// Validation and conversion for the configuration area. Pure, so the awkward
// cases can be tested without a form.
//
// ── What belongs here, and what deliberately does not ───────────────────────
//
// Services, normal working hours, and booking mode. NOT one-off blocking —
// lunch, holidays, a Tuesday off. The design doc puts those in calendar mode,
// and it is right: blocking an afternoon is something you do while looking at
// the afternoon, not something you go into settings to arrange.
//
// Nor stock, payroll, marketing or reporting. The brief's scope-control
// section lists those explicitly, and a setup screen is where such things
// arrive one checkbox at a time.

export type ServiceDraft = {
  name: string;
  durationMinutes: number;
  description?: string;
  /** Minor units. Kept as a string in the form and parsed here, because "35.5"
   *  and "35.50" and "£35.50" are all things people type. */
  priceMinor?: number | null;
  currency?: string | null;
};

export type ServiceProblem = "name" | "duration" | "price";

/** What is wrong with a draft service, if anything. */
export function validateService(draft: {
  name: string; durationMinutes: number | null; priceText?: string;
}): ServiceProblem[] {
  const problems: ServiceProblem[] = [];
  if (draft.name.trim().length < 2) problems.push("name");
  // Duration is load-bearing: the availability engine consumes it, so a
  // zero-length service would offer infinite slots that book no time.
  if (!draft.durationMinutes || draft.durationMinutes <= 0 || draft.durationMinutes > 8 * 60) {
    problems.push("duration");
  }
  if (draft.priceText != null && draft.priceText.trim() !== "" && parsePrice(draft.priceText) === null) {
    problems.push("price");
  }
  return problems;
}

/**
 * Money in, minor units out.
 *
 * Integers because floating-point money is a rounding error waiting to be
 * discovered by somebody's accountant. Returns null for anything unparseable
 * rather than guessing — a price the business did not mean is worse than a
 * price they have to retype.
 */
export function parsePrice(text: string): number | null {
  const cleaned = text.replace(/[£$€,\s]/g, "");
  if (cleaned === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

export function formatPrice(minor?: number | null, currency?: string | null): string {
  if (minor == null) return "";
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency ?? "GBP" })
    .format(minor / 100);
}

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

export type DayHours = {
  /** 0 = Monday, matching the calendar's documented default. */
  weekday: number;
  open: boolean;
  /** "09:00" — kept as text because that is what a time input gives back, and
   *  converting once at the edge beats converting at every keystroke. */
  from: string;
  to: string;
};

export function defaultHours(): DayHours[] {
  return WEEKDAYS.map((_, weekday) => ({
    weekday,
    // Closed at the weekend by default. A business that works Saturdays will
    // say so; one that does not should not have to turn two days off.
    open: weekday < 5,
    from: "09:00",
    to: "17:00",
  }));
}

export function minutesFromText(text: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (hours > 23 || mins > 59) return null;
  return hours * 60 + mins;
}

export type HoursProblem = { weekday: number; reason: "unreadable" | "backwards" };

/** Which days are set to something that cannot work. */
export function validateHours(hours: DayHours[]): HoursProblem[] {
  const problems: HoursProblem[] = [];
  for (const day of hours) {
    if (!day.open) continue;
    const from = minutesFromText(day.from);
    const to = minutesFromText(day.to);
    if (from === null || to === null) { problems.push({ weekday: day.weekday, reason: "unreadable" }); continue; }
    // A day that ends before it starts would produce no availability at all,
    // silently — the business would simply never appear bookable.
    if (to <= from) problems.push({ weekday: day.weekday, reason: "backwards" });
  }
  return problems;
}

/** The shape @nulife/booking's `setAvailability` expects. */
export function toAvailabilityRules(hours: DayHours[]): { weekday: number; startMinute: number; endMinute: number }[] {
  return hours
    .filter(d => d.open)
    .map(d => ({
      weekday: d.weekday,
      startMinute: minutesFromText(d.from) ?? 0,
      endMinute: minutesFromText(d.to) ?? 0,
    }))
    .filter(r => r.endMinute > r.startMinute);
}

export function textFromMinutes(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * The inverse of `toAvailabilityRules` — what the setup form should show for
 * hours already saved.
 *
 * A day with no rule is CLOSED, not "09:00 to 17:00 and switched off". The
 * distinction matters when the business reopens a day: it should come back to
 * something sensible rather than to 00:00–00:00, so closed days carry the
 * default times with `open: false`.
 *
 * A day with several periods (a lunch break, say) is collapsed to its outer
 * bounds, because the form can only express one span per day. That is a
 * genuine loss, so it is stated here rather than discovered: saving the form
 * afterwards would flatten the break. Multi-period days need a form that can
 * show them before this becomes safe to round-trip.
 */
export function fromAvailabilityRules(
  rules: { weekday: number; startMinute: number; endMinute: number }[],
): DayHours[] {
  return defaultHours().map(day => {
    const forDay = rules.filter(r => r.weekday === day.weekday);
    if (forDay.length === 0) return { ...day, open: false };
    return {
      weekday: day.weekday,
      open: true,
      from: textFromMinutes(Math.min(...forDay.map(r => r.startMinute))),
      to: textFromMinutes(Math.max(...forDay.map(r => r.endMinute))),
    };
  });
}

export const BOOKING_MODES = ["request", "instant"] as const;
export type BookingMode = (typeof BOOKING_MODES)[number];

/**
 * What each mode actually does, in the words the business will read.
 *
 * Written here rather than in the component so the description and the
 * behaviour stay together: instant booking means a stranger can occupy the
 * diary without anyone approving it, and that is worth saying plainly before
 * somebody switches it on.
 */
export const BOOKING_MODE_COPY: Record<BookingMode, { title: string; detail: string }> = {
  request: {
    title: "Ask me first",
    detail: "Customers request a time and you accept or decline. Nothing goes in your diary until you say so.",
  },
  instant: {
    title: "Book automatically",
    detail: "Available times are booked straight away, without asking you. Quicker for customers, and you find out afterwards.",
  },
};
